import React, { useEffect, useState } from "react";
import { Text, LoadingOverlay, Stack, Center } from "@mantine/core";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
  SearchInput,
  Button,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  IconButton,
  Skeleton,
  Tag,
} from "@jem-open/jem-ui";
import {
  MapPinOff,
  Eye,
  Settings,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  ChevronUp,
  Bell,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useExceptionCountApiService } from "../services/api/AttendanceExceptionsApiService";
import PageContainer from "../../../themes/components/PageContainer";
import PageHeading from "../../../themes/components/PageHeading";
import useShiftSitesHierarchyOverview from "../hooks/useShiftSitesHierarchyOverview";
import DateRangeFilter from "../components/DateRangeFilter";
import SummaryCardsRow from "../components/SummaryCardsRow";
import AdherenceCell from "../components/AdherenceCell";
import {
  ShiftSiteGroupOverview,
  ShiftSiteOverviewRow,
} from "../services/model/ShiftSite";
import { LocationSummary } from "../services/model/ShiftLocation";
import {
  DateRangeFilter as DateRangeFilterType,
  formatDateLabel,
} from "../utils/dateRangeUtils";
import AccessGuard from "../../../common/uac/AccessGuard";

const ShiftSitesOverviewPageInner: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const {
    siteGroups,
    ungrouped,
    isLoading,
    isFetching,
    isPreviousData,
    error,
    dateRange,
    setDateRange,
    customRange,
    setCustomRange,
    applyCustomRange,
    clientFilter,
    setClientFilter,
    searchQuery,
    setSearchQuery,
    clients,
    dateBounds,
    isTodayView,
    getSiteRowStats,
    getLocationRowStats,
    getGroupRowStats,
    summaryStats,
  } = useShiftSitesHierarchyOverview();

  // Scope the open-exceptions count to the selected date range so the summary
  // card matches the per-site Exceptions column instead of showing the global
  // all-time backlog on every date.
  const exceptionCountQuery = useExceptionCountApiService({
    status: "open",
    date_from: dateBounds.from,
    date_to: dateBounds.to,
  });
  const unresolvedExceptionCount = exceptionCountQuery.data?.count ?? 0;

  // Track which site groups are expanded (default: all collapsed)
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Pagination state
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  const toggleGroup = (groupId: string): void => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  // Initialize filter state from URL params on mount
  useEffect(() => {
    const dateFrom = searchParams.get("date_from");
    const dateTo = searchParams.get("date_to");
    const client = searchParams.get("client");
    const search = searchParams.get("search");
    const range = searchParams.get("range");

    const validRanges: DateRangeFilterType[] = [
      "today",
      "yesterday",
      "this-week",
      "this-month",
      "custom",
    ];
    if (range && validRanges.includes(range as DateRangeFilterType)) {
      if (range !== "custom") {
        setDateRange(range as DateRangeFilterType);
      }
    }

    if (dateFrom && dateTo && range === "custom") {
      const from = new Date(dateFrom);
      const to = new Date(dateTo);
      if (!Number.isNaN(from.getTime()) && !Number.isNaN(to.getTime())) {
        setDateRange("custom");
        setCustomRange([from, to]);
        applyCustomRange(from, to);
      }
    }

    if (client) setClientFilter(client);
    if (search) setSearchQuery(search);
    // eslint-disable-next-line
  }, []);

  const handleNavigateToSite = (siteId: string): void => {
    const params = new URLSearchParams();
    params.set("date_from", dateBounds.from);
    params.set("date_to", dateBounds.to);
    params.set("range", dateRange);
    if (clientFilter) params.set("client", clientFilter);
    navigate(`/time-attendance/shift-site/${siteId}?${params.toString()}`);
  };

  const handleNavigateToLocation = (locationId: string): void => {
    const params = new URLSearchParams();
    params.set("date_from", dateBounds.from);
    params.set("date_to", dateBounds.to);
    params.set("range", dateRange);
    if (clientFilter) params.set("client", clientFilter);
    navigate(`/time-attendance/${locationId}?${params.toString()}`);
  };

  const clientOptions = clients.map((c: { id: string; name: string }) => ({
    value: c.id,
    label: c.name,
  }));

  const totalSites = siteGroups.reduce((sum, g) => sum + g.sites.length, 0);
  const hasData = totalSites > 0 || ungrouped.length > 0;

  // Pagination: paginate at group level, ungrouped treated as one trailing group
  const totalGroups = siteGroups.length + (ungrouped.length > 0 ? 1 : 0);
  const totalPages = Math.max(1, Math.ceil(totalGroups / rowsPerPage));
  const startIdx = (currentPage - 1) * rowsPerPage;
  const endIdx = startIdx + rowsPerPage;
  const paginatedGroups = siteGroups.slice(startIdx, endIdx);
  // Show ungrouped only if it falls within the current page range
  const showUngrouped = ungrouped.length > 0 && endIdx > siteGroups.length;

  // Resolve the shift-code label for a row given its own active shift name.
  // Returns null whenever we can't produce a code (configs missing, no active
  // shift, or no config matches the active shift name) so callers can chain
  // their own fallbacks via `??`.
  const getShiftCodeFor = (
    configs: { name: string; code: string }[] | undefined,
    shiftName: string | null
  ): string | null => {
    if (!configs || !shiftName) return null;
    const match = configs.find((c) => c.name === shiftName);
    return match?.code ?? null;
  };

  const renderDirectLocationRow = (location: LocationSummary): JSX.Element => {
    const stats = getLocationRowStats(location);
    const { rostered, onSite, covered, exceptions, adherence } = stats;
    return (
      <TableRow
        key={location.id}
        className="border-b border-[#cecece] bg-white"
      >
        <TableCell className="py-5" style={{ paddingLeft: 56 }}>
          <span className="text-sm text-[#062133]">{location.name}</span>
          {location.region && (
            <span className="text-xs text-[#839099] ml-2">
              {location.region}
            </span>
          )}
        </TableCell>
        <TableCell className="py-5">
          <span className="text-sm text-[#384d5c]">
            {getShiftCodeFor(location.shift_configs, stats.activeShiftName) ??
              "-"}
          </span>
        </TableCell>
        <TableCell className="py-5">
          <span className="text-sm text-[#384d5c]">-</span>
        </TableCell>
        <TableCell className="py-5">
          <span className="text-sm text-[#384d5c]">{rostered}</span>
        </TableCell>
        <TableCell className="py-5">
          <span className="text-sm text-[#384d5c]">{covered}</span>
          {isTodayView && onSite !== covered && (
            <span className="ml-2 text-xs text-[#839099]">
              {onSite} on site now
            </span>
          )}
        </TableCell>
        <TableCell className="py-5">
          <span className="text-sm text-[#384d5c]">{exceptions}</span>
        </TableCell>
        <TableCell className="py-5">
          <AdherenceCell value={adherence} />
        </TableCell>
        <TableCell className="py-5">
          <div className="flex gap-2">
            <IconButton
              variant="ghost"
              size="sm"
              onClick={() => handleNavigateToLocation(location.id)}
            >
              <Eye size={16} color="#384d5c" />
            </IconButton>
            <IconButton
              variant="ghost"
              size="sm"
              aria-label={`Configure ${location.name}`}
              onClick={() =>
                navigate(
                  location.site
                    ? `/site-configuration/${location.site}`
                    : `/time-attendance/${location.id}/config`
                )
              }
            >
              <Settings size={16} color="#384d5c" />
            </IconButton>
          </div>
        </TableCell>
      </TableRow>
    );
  };

  const renderSiteRow = (
    site: ShiftSiteOverviewRow,
    topLevel = false
  ): JSX.Element => {
    const stats = getSiteRowStats(site);
    const { rostered, onSite, covered, exceptions, adherence } = stats;

    return (
      <TableRow key={site.id} className="border-b border-[#cecece] bg-white">
        <TableCell className="py-5" style={{ paddingLeft: topLevel ? 24 : 56 }}>
          <span className="text-sm text-[#062133]">{site.name}</span>
        </TableCell>
        <TableCell className="py-5">
          <span className="text-sm text-[#384d5c]">
            {getShiftCodeFor(site.shift_configs, stats.activeShiftName) ??
              site.current_shift ??
              "-"}
          </span>
        </TableCell>
        <TableCell className="py-5">
          <span className="text-sm text-[#384d5c]">{site.rosters_count}</span>
        </TableCell>
        <TableCell className="py-5">
          <span className="text-sm text-[#384d5c]">{rostered}</span>
        </TableCell>
        <TableCell className="py-5">
          <span className="text-sm text-[#384d5c]">{covered}</span>
          {isTodayView && onSite !== covered && (
            <span className="ml-2 text-xs text-[#839099]">
              {onSite} on site now
            </span>
          )}
        </TableCell>
        <TableCell className="py-5">
          <span className="text-sm text-[#384d5c]">{exceptions}</span>
        </TableCell>
        <TableCell className="py-5">
          <AdherenceCell value={adherence} />
        </TableCell>
        <TableCell className="py-5">
          <div className="flex gap-2">
            <IconButton
              variant="ghost"
              size="sm"
              onClick={() => handleNavigateToSite(site.id)}
            >
              <Eye size={16} color="#384d5c" />
            </IconButton>
            <IconButton
              variant="ghost"
              size="sm"
              aria-label={`Configure ${site.name}`}
              onClick={() => navigate(`/site-configuration/${site.id}`)}
            >
              <Settings size={16} color="#384d5c" />
            </IconButton>
          </div>
        </TableCell>
      </TableRow>
    );
  };

  const renderGroupRow = (group: ShiftSiteGroupOverview): JSX.Element => {
    const groupKey = group.id || group.name;
    const isCollapsed = !expandedGroups.has(groupKey);
    const ChevronIcon = isCollapsed ? ChevronRight : ChevronDown;
    const { rostered, onSite, covered, exceptions, adherence } =
      getGroupRowStats(group);

    return (
      <React.Fragment key={groupKey}>
        <TableRow
          className="border-b border-[#cecece] bg-white cursor-pointer hover:bg-gray-50"
          onClick={() => toggleGroup(groupKey)}
        >
          <TableCell className="pl-3 py-5">
            <div className="flex items-center gap-2">
              <ChevronIcon size={16} color="#384d5c" />
              <span className="text-sm font-semibold text-[#062133]">
                {group.name}
              </span>
            </div>
          </TableCell>
          <TableCell className="py-5" />
          <TableCell className="py-5" />
          <TableCell className="py-5">
            <span className="text-sm text-[#384d5c]">{rostered}</span>
          </TableCell>
          <TableCell className="py-5">
            <span className="text-sm text-[#384d5c]">{covered}</span>
            {isTodayView && onSite !== covered && (
              <span className="ml-2 text-xs text-[#839099]">
                {onSite} on site now
              </span>
            )}
          </TableCell>
          <TableCell className="py-5">
            <span className="text-sm text-[#384d5c]">{exceptions}</span>
          </TableCell>
          <TableCell className="py-5">
            <AdherenceCell value={adherence} />
          </TableCell>
          <TableCell className="py-5" />
        </TableRow>
        {!isCollapsed && group.sites.map((site) => renderSiteRow(site))}
        {!isCollapsed && group.direct_locations?.map(renderDirectLocationRow)}
      </React.Fragment>
    );
  };

  const renderUngroupedRow = (location: LocationSummary): JSX.Element => {
    const stats = getLocationRowStats(location);
    const { rostered, onSite, covered, exceptions, adherence } = stats;
    return (
      <TableRow
        key={location.id}
        className="border-b border-[#cecece] bg-white"
      >
        <TableCell className="pl-6 py-5">
          <span className="text-sm text-[#062133]">{location.name}</span>
          {location.region && (
            <span className="text-xs text-[#839099] ml-2">
              {location.region}
            </span>
          )}
        </TableCell>
        <TableCell className="py-5">
          <span className="text-sm text-[#384d5c]">
            {getShiftCodeFor(location.shift_configs, stats.activeShiftName) ??
              "-"}
          </span>
        </TableCell>
        <TableCell className="py-5">
          <span className="text-sm text-[#384d5c]">-</span>
        </TableCell>
        <TableCell className="py-5">
          <span className="text-sm text-[#384d5c]">{rostered}</span>
        </TableCell>
        <TableCell className="py-5">
          <span className="text-sm text-[#384d5c]">{covered}</span>
          {isTodayView && onSite !== covered && (
            <span className="ml-2 text-xs text-[#839099]">
              {onSite} on site now
            </span>
          )}
        </TableCell>
        <TableCell className="py-5">
          <span className="text-sm text-[#384d5c]">{exceptions}</span>
        </TableCell>
        <TableCell className="py-5">
          <AdherenceCell value={adherence} />
        </TableCell>
        <TableCell className="py-5">
          <div className="flex gap-2">
            <IconButton
              variant="ghost"
              size="sm"
              onClick={() => handleNavigateToLocation(location.id)}
            >
              <Eye size={16} color="#384d5c" />
            </IconButton>
            <IconButton
              variant="ghost"
              size="sm"
              aria-label={`Configure ${location.name}`}
              onClick={() =>
                navigate(
                  location.site
                    ? `/site-configuration/${location.site}`
                    : `/time-attendance/${location.id}/config`
                )
              }
            >
              <Settings size={16} color="#384d5c" />
            </IconButton>
          </div>
        </TableCell>
      </TableRow>
    );
  };

  const renderContent = (): JSX.Element | null => {
    if (isLoading) {
      return (
        <div className="space-y-4 mt-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16 w-full rounded-lg" />
          ))}
        </div>
      );
    }

    if (error) {
      return (
        <Center style={{ height: 300 }}>
          <Stack align="center" spacing="md">
            <MapPinOff size={48} color="#9CA3AF" />
            <Text color="dimmed">
              Failed to load locations. Please try again.
            </Text>
          </Stack>
        </Center>
      );
    }

    if (!hasData) {
      return (
        <Center style={{ height: 300 }}>
          <Stack align="center" spacing="md">
            <MapPinOff size={48} color="#9CA3AF" />
            <Text color="dimmed">No shift locations configured.</Text>
            <Text size="sm" color="dimmed">
              Contact your administrator to set up shift locations.
            </Text>
          </Stack>
        </Center>
      );
    }

    return (
      <>
        {/* Phone card list — sm:hidden */}
        <div className="sm:hidden flex flex-col gap-md">
          {paginatedGroups.map((group) => {
            const groupKey = group.id || group.name;
            if (group.id === null) {
              // Ungrouped — render each site as a top-level card
              return (
                <div key={groupKey} className="flex flex-col gap-sm">
                  {group.sites.map((site) => {
                    const stats = getSiteRowStats(site);
                    return (
                      <button
                        key={site.id}
                        type="button"
                        onClick={() => handleNavigateToSite(site.id)}
                        className="w-full rounded-lg border border-greyscale-border bg-white p-sm text-left shadow-sm"
                      >
                        <div className="flex items-center justify-between gap-sm">
                          <span className="text-sm font-semibold text-greyscale-text-title truncate min-w-0 flex-1">
                            {site.name}
                          </span>
                          <AdherenceCell value={stats.adherence} />
                        </div>
                        <div className="flex flex-wrap items-center gap-x-md gap-y-xxxs mt-xs text-xs text-greyscale-text-caption">
                          <span>
                            {stats.onSite}/{stats.rostered} in
                          </span>
                          <span>{stats.noShow} no-show</span>
                          <span>{stats.lateArrival} late</span>
                        </div>
                        <div className="flex items-center justify-end gap-xxs mt-sm text-xs font-semibold text-secondary-pink-600">
                          Open site
                          <ChevronRight size={14} aria-hidden="true" />
                        </div>
                      </button>
                    );
                  })}
                </div>
              );
            }
            const isCollapsed = !expandedGroups.has(groupKey);
            const groupStats = getGroupRowStats(group);
            return (
              <div key={groupKey} className="flex flex-col gap-sm">
                <button
                  type="button"
                  onClick={() => toggleGroup(groupKey)}
                  aria-expanded={!isCollapsed}
                  className="w-full text-left pb-xs border-b border-greyscale-border"
                >
                  <div className="flex items-center justify-between gap-sm">
                    <span className="text-base font-semibold text-greyscale-text-title truncate min-w-0 flex-1">
                      {group.name}
                    </span>
                    <div className="flex items-center gap-sm shrink-0">
                      <AdherenceCell value={groupStats.adherence} />
                      {isCollapsed ? (
                        <ChevronDown
                          size={16}
                          className="text-greyscale-text-caption"
                          aria-hidden="true"
                        />
                      ) : (
                        <ChevronUp
                          size={16}
                          className="text-greyscale-text-caption"
                          aria-hidden="true"
                        />
                      )}
                    </div>
                  </div>
                  <div className="mt-xs text-xs text-greyscale-text-caption">
                    {groupStats.onSite}/{groupStats.rostered} in ·{" "}
                    {groupStats.noShow} no-show · {groupStats.lateArrival} late
                  </div>
                </button>
                {!isCollapsed && (
                  <div className="flex flex-col gap-sm">
                    {group.sites.map((site) => {
                      const stats = getSiteRowStats(site);
                      return (
                        <button
                          key={site.id}
                          type="button"
                          onClick={() => handleNavigateToSite(site.id)}
                          className="w-full rounded-lg border border-greyscale-border bg-white p-sm text-left shadow-sm"
                        >
                          <div className="flex items-center justify-between gap-sm">
                            <span className="text-sm font-semibold text-greyscale-text-title truncate min-w-0 flex-1">
                              {site.name}
                            </span>
                            <AdherenceCell value={stats.adherence} />
                          </div>
                          <div className="flex flex-wrap items-center gap-x-md gap-y-xxxs mt-xs text-xs text-greyscale-text-caption">
                            <span>
                              {stats.onSite}/{stats.rostered} in
                            </span>
                            <span>{stats.noShow} no-show</span>
                            <span>{stats.lateArrival} late</span>
                          </div>
                          <div className="flex items-center justify-end gap-xxs mt-sm text-xs font-semibold text-secondary-pink-600">
                            Open site
                            <ChevronRight size={14} aria-hidden="true" />
                          </div>
                        </button>
                      );
                    })}
                    {group.direct_locations?.map((location) => {
                      const stats = getLocationRowStats(location);
                      return (
                        <button
                          key={location.id}
                          type="button"
                          onClick={() => handleNavigateToLocation(location.id)}
                          className="w-full rounded-lg border border-greyscale-border bg-white p-sm text-left shadow-sm"
                        >
                          <div className="flex items-center justify-between gap-sm">
                            <span className="text-sm font-semibold text-greyscale-text-title truncate min-w-0 flex-1">
                              {location.name}
                            </span>
                            <AdherenceCell value={stats.adherence} />
                          </div>
                          <div className="flex flex-wrap items-center gap-x-md gap-y-xxxs mt-xs text-xs text-greyscale-text-caption">
                            <span>
                              {stats.onSite}/{stats.rostered} in
                            </span>
                            <span>{stats.noShow} no-show</span>
                            <span>{stats.lateArrival} late</span>
                          </div>
                          <div className="flex items-center justify-end gap-xxs mt-sm text-xs font-semibold text-secondary-pink-600">
                            Open site
                            <ChevronRight size={14} aria-hidden="true" />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          {showUngrouped &&
            ungrouped.map((location) => {
              const stats = getLocationRowStats(location);
              return (
                <button
                  key={location.id}
                  type="button"
                  onClick={() => handleNavigateToLocation(location.id)}
                  className="w-full rounded-lg border border-greyscale-border bg-white p-sm text-left shadow-sm"
                >
                  <div className="flex items-center justify-between gap-sm">
                    <span className="text-sm font-semibold text-greyscale-text-title truncate min-w-0 flex-1">
                      {location.name}
                    </span>
                    <AdherenceCell value={stats.adherence} />
                  </div>
                  <div className="flex flex-wrap items-center gap-x-md gap-y-xxxs mt-xs text-xs text-greyscale-text-caption">
                    <span>
                      {stats.onSite}/{stats.rostered} in
                    </span>
                    <span>{stats.noShow} no-show</span>
                    <span>{stats.lateArrival} late</span>
                  </div>
                  <div className="flex items-center justify-end gap-xxs mt-sm text-xs font-semibold text-secondary-pink-600">
                    Open site
                    <ChevronRight size={14} aria-hidden="true" />
                  </div>
                </button>
              );
            })}
        </div>

        {/* Phone pagination — same state as desktop, hidden on tablet+ and when there's only one page */}
        {totalPages > 1 && (
          <div className="sm:hidden flex items-center justify-between gap-sm mt-md">
            <span className="text-sm text-greyscale-text-caption">
              {startIdx + 1}-{Math.min(endIdx, totalGroups)} of {totalGroups}
            </span>
            <div className="flex gap-xs">
              <IconButton
                variant="ghost"
                size="sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                aria-label="Previous page"
                className="h-11 w-11"
              >
                <ChevronLeft size={16} />
              </IconButton>
              <IconButton
                variant="ghost"
                size="sm"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
                aria-label="Next page"
                className="h-11 w-11"
              >
                <ChevronRight size={16} />
              </IconButton>
            </div>
          </div>
        )}

        {/* Desktop table — hidden sm:block */}
        <div className="hidden sm:block border border-[#cecece] rounded-lg overflow-hidden">
          <Table style={{ tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: "30%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "8%" }} />
            </colgroup>
            <TableHeader>
              <TableRow className="bg-[#fbfbfb] border-b border-[#cecece]">
                <TableHead className="pl-6 py-4 text-xs font-semibold uppercase tracking-wider text-[#6a7a85]">
                  Region/Site
                </TableHead>
                <TableHead className="py-4 text-xs font-semibold uppercase tracking-wider text-[#6a7a85]">
                  Current Shift
                </TableHead>
                <TableHead className="py-4 text-xs font-semibold uppercase tracking-wider text-[#6a7a85]">
                  Rosters Per Site
                </TableHead>
                <TableHead className="py-4 text-xs font-semibold uppercase tracking-wider text-[#6a7a85]">
                  Rostered Employees
                </TableHead>
                <TableHead className="py-4 text-xs font-semibold uppercase tracking-wider text-[#6a7a85]">
                  {isTodayView ? "Covered" : "Clocked In"}
                </TableHead>
                <TableHead className="py-4 text-xs font-semibold uppercase tracking-wider text-[#6a7a85]">
                  Exceptions
                </TableHead>
                <TableHead className="py-4 text-xs font-semibold uppercase tracking-wider text-[#6a7a85]">
                  Roster Adherence
                </TableHead>
                <TableHead className="py-4 text-xs font-semibold uppercase tracking-wider text-[#6a7a85]">
                  Action
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedGroups.map((group) => {
                if (group.id === null) {
                  // Ungrouped sites: render each site as a top-level row
                  return group.sites.map((site) => renderSiteRow(site, true));
                }
                return renderGroupRow(group);
              })}
              {showUngrouped && ungrouped.map(renderUngroupedRow)}
            </TableBody>
          </Table>
          <div className="flex items-center justify-end gap-6 mt-4">
            <div className="flex items-center gap-3">
              <span className="text-sm text-[#6a7a85]">Rows per page:</span>
              <Select
                value={String(rowsPerPage)}
                onValueChange={(v) => {
                  setRowsPerPage(Number(v));
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="w-[70px] h-[32px] border-[#cecece] rounded-lg">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <span className="text-sm text-[#6a7a85]">
              {startIdx + 1}-{Math.min(endIdx, totalGroups)} of {totalGroups}
            </span>
            <div className="flex gap-1">
              <IconButton
                variant="ghost"
                size="sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                aria-label="Previous page"
                className="h-11 w-11 sm:h-8 sm:w-8"
              >
                <ChevronLeft size={16} />
              </IconButton>
              <IconButton
                variant="ghost"
                size="sm"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
                aria-label="Next page"
                className="h-11 w-11 sm:h-8 sm:w-8"
              >
                <ChevronRight size={16} />
              </IconButton>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <PageContainer>
      {/*
        Show the overlay on the first fetch (`isLoading`) and on user-initiated
        refetches such as a date-range or client change. Those change the query
        key, so under `keepPreviousData` `isPreviousData` is TRUE while the new
        data loads → overlay shows (feedback on a date change). Silent 30s
        background polls reuse the SAME query key, so `isPreviousData` is FALSE
        while `isFetching` is true → the overlay stays hidden, avoiding a flash
        on every poll.
      */}
      <LoadingOverlay visible={isLoading || (isFetching && isPreviousData)} />
      <PageHeading title="Time & Attendance" />
      <Text size="sm" color="dimmed" className="mb-4">
        Track attendance in real time and monitor hours worked on site.
      </Text>

      {/* Filters row */}
      <div className="flex flex-col gap-sm mb-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-xs sm:flex-row sm:items-center sm:gap-sm">
          <DateRangeFilter
            value={dateRange}
            onChange={setDateRange}
            customRange={customRange}
            onCustomRangeChange={setCustomRange}
            onApply={() =>
              applyCustomRange(
                customRange[0] ?? new Date(),
                customRange[1] ?? new Date()
              )
            }
          />
          {clientOptions.length > 0 && (
            <Select
              value={clientFilter || ""}
              onValueChange={(val) => setClientFilter(val === "" ? null : val)}
            >
              <SelectTrigger className="w-full sm:w-[160px] h-[46px] border-[#cecece] rounded-lg">
                <SelectValue placeholder="All Clients" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Clients</SelectItem>
                {clientOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        <SearchInput
          placeholder="Search client, site, location"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onClear={() => setSearchQuery("")}
          className="w-full sm:w-[280px]"
        />
      </div>

      {/* Date label + View Exceptions */}
      <div className="flex flex-col gap-xs mb-4 sm:flex-row sm:items-center sm:justify-between">
        <Text size="sm" color="dimmed">
          Showing data for:{" "}
          <Text component="span" weight={600} color="dark">
            {formatDateLabel(dateBounds.from, dateBounds.to)}
          </Text>
        </Text>
        <Button
          variant="primary"
          onClick={() => navigate("/time-attendance/exceptions")}
          className="relative w-full sm:w-auto"
        >
          <Bell size={14} className="mr-xxs" />
          View Exceptions
          {unresolvedExceptionCount > 0 && (
            <Tag
              variant="failed"
              size="sm"
              className="absolute -top-2 -right-2 min-w-[20px] justify-center"
              aria-label={`${unresolvedExceptionCount} unresolved exceptions`}
            >
              {unresolvedExceptionCount}
            </Tag>
          )}
        </Button>
      </div>

      {/* Summary cards */}
      {!isLoading && hasData && (
        <SummaryCardsRow
          totalScheduled={summaryStats.totalScheduled}
          totalScheduledToDate={summaryStats.totalScheduledToDate}
          totalClockedIn={summaryStats.totalClockedIn}
          totalNoShows={summaryStats.totalNoShows}
          totalLateArrivals={summaryStats.totalLateArrivals}
          regionsActive={summaryStats.regionsActive}
          totalRegions={summaryStats.totalRegions}
          totalSites={totalSites + ungrouped.length}
        />
      )}

      {renderContent()}
    </PageContainer>
  );
};

const ShiftSitesOverviewPage: React.FC = () => (
  <AccessGuard>
    <ShiftSitesOverviewPageInner />
  </AccessGuard>
);

export default ShiftSitesOverviewPage;
